import { parseDateOnly } from '@/lib/utils/date-only'
import { useShipEstimateHistory } from '../api/queries'
import type { ShipConfirmationTracker } from '../api/types'

function formatDate(iso: string): string {
  try {
    return parseDateOnly(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return iso
  }
}

function formatLoggedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function changeText(days: number | null): string | null {
  if (days == null || days === 0) return null
  return days > 0 ? `+${days}d later` : `${days}d earlier`
}

/**
 * Every estimate logged on one line (and its splits), newest first —
 * `GET /api/po-shipment-confirmation/{id}/estimates`. Read-only; logging a
 * new estimate stays on the row's "Log estimate" action. Fetches lazily,
 * only while the row is expanded.
 */
export function EstimateHistoryPanel({ tracker }: { tracker: ShipConfirmationTracker }) {
  const { data, isLoading, isError } = useShipEstimateHistory(tracker.id)

  return (
    <div className="px-[var(--cell-padding-x)] py-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-tertiary">
        Estimate history
      </div>
      {isLoading ? (
        <div className="text-sm text-tertiary">Loading estimates…</div>
      ) : isError ? (
        <div className="text-sm text-danger-text">Couldn't load the estimate history.</div>
      ) : !data || data.length === 0 ? (
        <div className="text-sm text-tertiary">No estimates logged.</div>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {data.map((e) => {
            const change = changeText(e.change_days)
            return (
              <li key={e.id} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="font-medium text-primary">{formatDate(e.estimated_date)}</span>
                {change ? (
                  <span className={(e.change_days ?? 0) > 0 ? 'text-warning-text' : 'text-success-text'}>
                    {change}
                  </span>
                ) : null}
                {e.split_index != null ? (
                  <span className="text-tertiary">Split {e.split_index}</span>
                ) : null}
                {e.note ? <span className="text-secondary">{e.note}</span> : null}
                <span className="text-tertiary">
                  · {e.logged_by ? `${e.logged_by.split('@')[0]}, ` : ''}
                  {formatLoggedAt(e.logged_at)}
                </span>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
